import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Users,
  Mic,
  Music,
  Megaphone,
  DollarSign,
  FileText,
  TrendingUp,
  User,
  LogOut,
  ChevronDown,
  X,
  Shield,
  UserCog,
  Sparkles,
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { useEffectivePermissions } from '../../hooks/useEffectivePermissions';
import { useAuth } from '../../hooks/useAuth';
import { useUserProfile } from '../../hooks/useUserProfile';
import { Brand } from './Brand';

export interface SidebarNavProps {
  mode: 'drawer' | 'static';
  isOpen?: boolean;
  onClose?: () => void;
  triggerRef?: React.RefObject<HTMLElement>;
}

export const SidebarNav: React.FC<SidebarNavProps> = ({ mode, isOpen = false, onClose, triggerRef }) => {
  const { isAdmin } = usePermissions();
  const { hasRole: effectiveHasRole } = useEffectivePermissions();
  const { user, signOut } = useAuth();
  const { profile } = useUserProfile();
  const location = useLocation();
  const navigate = useNavigate();
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const openedRef = useRef(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const navItems = [
    { path: '/', icon: Home, label: 'Dashboard' },
    { path: '/contacts', icon: Users, label: 'Contacts' },
    { path: '/artists', icon: Mic, label: 'Artists' },
    { path: '/releases', icon: Music, label: 'Releases' },
    { path: '/campaigns', icon: Megaphone, label: 'Campaigns' },
    { path: '/meta/alignment', icon: Sparkles, label: 'Meta Alignment' },
  ];

  const toolItems = [
    { path: '/budget', icon: DollarSign, label: 'Budget Manager' },
    { path: '/reports', icon: FileText, label: 'Reports' },
    { path: '/datauploads', icon: TrendingUp, label: 'Data Uploads' },
  ];


  // Close on Escape and click outside (drawer mode)
  useEffect(() => {
    if (mode !== 'drawer' || !isOpen) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
    };
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) onClose?.();
    };

    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClickOutside);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClickOutside);
    };
  }, [mode, isOpen, onClose]);

  // Body scroll lock when open (drawer mode)
  useEffect(() => {
    if (mode !== 'drawer') return;
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mode, isOpen]);

  // Focus management (drawer mode)
  useEffect(() => {
    if (mode !== 'drawer') return;
    if (isOpen) {
      openedRef.current = true;
      setTimeout(() => closeBtnRef.current?.focus(), 0);
    } else if (openedRef.current) {
      openedRef.current = false;
      triggerRef?.current?.focus?.();
    }
  }, [mode, isOpen, triggerRef]);

  // Auto-close drawer after navigation completes
  useEffect(() => {
    if (mode === 'drawer' && isOpen) {
      onClose?.();
    }
  }, [location.pathname]);

  const Section = ({ title }: { title: string }) => (
    <h3 className="px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
      {title}
    </h3>
  );

  // Accept `icon` from item objects and alias to `Icon` component prop
  const Item = ({ path, label, icon: Icon, active }: any) => {
    const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      navigate(path);
      if (mode === 'drawer') onClose?.();
    };
    return (
      <a
        href={path}
        onClick={onClick}
        className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors relative ${
          active
            ? 'text-blue-700 dark:text-blue-300'
            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
        }`}
      >
        {/* Leading active bar */}
        <span className={`pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 h-5 w-0.5 rounded bg-blue-500 transition-opacity ${
          active ? 'opacity-100' : 'opacity-0'
        }`} />
        <Icon className={`mr-3 h-5 w-5 ${active ? 'text-blue-500 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400'}`} />
        <span className="truncate">{label}</span>
      </a>
    );
  };

  const PanelInner = (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center">
        {mode === 'drawer' && (
          <button
            ref={closeBtnRef}
            onClick={onClose}
            aria-label="Close navigation"
            className="rounded-md p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <X className="h-5 w-5" />
          </button>
        )}
        <div className="flex-1 flex items-center justify-center">
          <Brand size="lg" />
        </div>
      </div>

      <div className="flex-1 px-4 py-6 overflow-y-auto">
        <div className="space-y-1">
          <Section title="Main" />
          {navItems.map((item) => (
            <Item key={item.path} {...item} active={location.pathname === item.path} />
          ))}
        </div>

        <div className="mt-8 space-y-1">
          <Section title="Tools" />
          {toolItems.map((item) => (
            <Item key={item.path} {...item} active={location.pathname.startsWith(item.path)} />
          ))}
        </div>

      </div>

      {/* User Section */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        {user ? (
          <div className="relative">
            <button
              onClick={() => setIsUserMenuOpen((v) => !v)}
              className="w-full flex items-center px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800 rounded-md transition-colors"
            >
              <div className="flex-shrink-0">
                <div className="h-8 w-8 rounded-full bg-blue-500 dark:bg-blue-600 flex items-center justify-center">
                  <span className="text-sm font-medium text-white">
                    {(profile?.first_name?.[0] || '') + (profile?.last_name?.[0] || user.email?.[0] || 'U')}
                  </span>
                </div>
              </div>
              <div className="ml-3 min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {profile?.first_name || profile?.last_name ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : (user.email?.split('@')[0] || 'User')}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
              </div>
              <ChevronDown className={`h-4 w-4 text-gray-400 dark:text-gray-500 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
            </button>
            {isUserMenuOpen && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-600 py-1 z-50">
                <a
                  href="/settings"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsUserMenuOpen(false);
                    navigate('/settings');
                    if (mode === 'drawer') onClose?.();
                  }}
                  className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <User className="mr-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                  Profile & Settings
                </a>
                
                {/* User Admin Section */}
                {(effectiveHasRole('admin') || effectiveHasRole('super')) && (
                  <>
                    <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>
                    <div className="px-4 py-2">
                      <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        User Admin
                      </div>
                    </div>
                    <a
                      href="/admin/users"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsUserMenuOpen(false);
                        navigate('/admin/users');
                        if (mode === 'drawer') onClose?.();
                      }}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <UserCog className="mr-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                      User Management
                    </a>
                    <a
                      href="/admin/roles"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsUserMenuOpen(false);
                        navigate('/admin/roles');
                        if (mode === 'drawer') onClose?.();
                      }}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <Shield className="mr-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                      Role Management
                    </a>
                  </>
                )}
                
                <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>
                <button
                  onClick={async () => {
                    setIsUserMenuOpen(false);
                    await signOut();
                  }}
                  className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <LogOut className="mr-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">Not signed in</div>
        )}
      </div>
    </div>
  );

  if (mode === 'static') {
    return (
  <nav className="hidden lg:flex relative glass e1 border-r border-gray-200/60 dark:border-gray-800/60 w-64 flex-shrink-0 min-h-screen z-50 bg-white dark:bg-gray-900" aria-label="Sidebar">
        {PanelInner}
      </nav>
    );
  }

  // Drawer mode
  if (!isOpen) return null;

  return (
    <div className="lg:hidden">
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} aria-hidden="true" />
      {/* Panel */}
      <div
        ref={containerRef}
        className="fixed inset-y-0 left-0 z-50 w-72 bg-white dark:bg-gray-900 shadow-xl border-r border-gray-200 dark:border-gray-700"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
      >
        {PanelInner}
      </div>
    </div>
  );
};

export default SidebarNav;
